/**
 * GutterBuilderPanel — Iter125
 *
 * Single-page per-pin gutter builder. Replaces the iter113 Spouting
 * Configure wizard. Layout mirrors Restoration → Concrete → Ridges
 * per-pin table.
 *
 * Top controls (1 row): Gutter Type · Profile · Material · Colour ·
 *                       Bracket Type · Bracket Finish.
 *
 * Per-pin table: Pin Ref · Measured · Override · Span · Brackets ·
 *                Mitred Y/N · Mitre Extra · Rate · Line $.
 *
 * Bracket section below the pin list shows the aggregated bracket
 * line + rate-detail (✎ Detail) overlay.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { QuoteTotalStrip } from '@/components/QuoteTotalStrip';
import SaveDocButton from './SaveDocButton';
import { Loader2, Save, RefreshCw, AlertCircle, Droplets, MapPin } from 'lucide-react';
import { RateDetailProvider, useRateDetail } from '@/components/RateDetailEditButton';
import { ConfidenceBanner } from '@/components/ConfidenceBanner';

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function GutterBuilderPanel({ api, appraisalId, onLinesChanged }) {
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState({ lines: [], total: 0, stats: {} });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const { data: d } = await api.get(
        `/guttering/appraisals/${appraisalId}/gutter-builder/config`,
      );
      setData(d);
      setDirty(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, [api, appraisalId]);

  const refreshPreview = useCallback(async (notifyParent = false, overrideConfig = null) => {
    try {
      // v10.29 Sprint 2 · B8 (2026-02) — when the operator changes
      // profile/material/outlet in the panel we want them to see the
      // new totals BEFORE committing a save. If `overrideConfig` is
      // passed we POST it as an override body so the preview
      // recalculates against the in-flight config; otherwise we hit
      // the plain GET which reads the persisted config.
      const url = `/guttering/appraisals/${appraisalId}/gutter-builder/preview`;
      const resp = overrideConfig
        ? await api.post(url, { config: overrideConfig })
        : await api.get(url);
      const p = resp.data;
      setPreview(p);
      // Iter128 fix — only ping the parent on EXPLICIT user save, not
      // on every render-triggered preview refresh. The previous code
      // called onLinesChanged() on every preview fetch which caused
      // the parent's reload() (new function reference each render) to
      // fire, remounting this panel and re-triggering the effect →
      // infinite flicker.
      if (notifyParent && onLinesChanged) onLinesChanged();
    } catch (e) { /* silent */ }
  }, [api, appraisalId, onLinesChanged]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [appraisalId]);
  // Fires once right after `load()` populates `data` → refreshes the
  // preview against the persisted config. The `dirty` guard prevents
  // this effect from clobbering the debounced reactive re-preview
  // below when the operator is mid-edit.
  useEffect(() => {
    if (data && !dirty) refreshPreview(false);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [data]);

  // v10.29 Sprint 2 · B8 (2026-02) — debounced reactive re-preview
  // when the operator changes profile / material / gutter_type /
  // bracket_finish / stories_override in the panel. Fires 350ms after
  // the last change so rapid keystrokes / dropdown-flicks don't spam
  // the endpoint. Sends the LOCAL config as an override so the
  // preview matches what the operator sees on-screen right now.
  const _liveCfg = data?.config;
  const _cfgSignature = _liveCfg ? [
    _liveCfg.stripping_enabled,
    _liveCfg.downpipe_material,
    _liveCfg.stripping_rate_per_m,
    _liveCfg.gutter_type,
    _liveCfg.material_key,
    _liveCfg.profile_key,
    _liveCfg.bracket_finish,
    _liveCfg.stories_override,
    _liveCfg.bracket_buffer_pct,
    _liveCfg.colour_id,
  ].join('|') : '';
  useEffect(() => {
    if (!_liveCfg || !dirty) return;
    const t = setTimeout(() => { refreshPreview(false, _liveCfg); }, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [_cfgSignature]);

  const save = useCallback(async (next) => {
    setBusy(true); setErr(null);
    try {
      const body = { ...next };
      // Merge per-pin overrides from the panel state.
      await api.put(`/guttering/appraisals/${appraisalId}/gutter-builder/config`, body);
      await refreshPreview(true);  // notify parent — explicit save
      setDirty(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [api, appraisalId, refreshPreview]);

  const update = useCallback((patch) => {
    setData(d => ({ ...d, config: { ...d.config, ...patch } }));
    setDirty(true);
  }, []);

  // Header profile / material: offer to clear per-run picks so every run follows the header.
  const applyHeader = useCallback((patch, field) => {
    const own = ((data?.config?.pins) || []).filter(p => p[field]);
    const word = field === 'material_key' ? 'material' : 'profile';
    if (own.length && window.confirm(`Apply to all runs?\n\n${own.length} run${own.length === 1 ? ' has its' : 's have their'} own ${word}. OK = every run follows the header. Cancel = keep those runs as they are.`)) {
      patch = { ...patch, pins: (data.config.pins || []).map(p => ({ ...p, [field]: null })) };
    }
    update(patch);
  }, [data, update]);

  const updatePin = useCallback((pin_id, raw_id, patch, autoSave = false) => {
    setData(d => {
      const existingPins = d.config.pins || [];
      const idx = existingPins.findIndex(p => p.pin_id === pin_id);
      let next;
      if (idx >= 0) {
        next = [...existingPins];
        next[idx] = { ...next[idx], ...patch, pin_id, raw_id };
      } else {
        next = [...existingPins, { pin_id, raw_id, ...patch }];
      }
      const nextConfig = { ...d.config, pins: next };
      // Iter130k — discrete toggles (e.g. Mitred) can request an
      // immediate save so the operator sees Line $ refresh without a
      // separate click on the Save button.
      if (autoSave) {
        setTimeout(() => save(nextConfig), 0);
      }
      return { ...d, config: nextConfig };
    });
    setDirty(true);
  }, [save]);

  // Iter130d — bulk-edit every diagram Spouting pin in one go.
  // `patchFor(diagPin, savedPin)` returns the per-pin patch to apply.
  const bulkUpdatePins = useCallback((patchFor) => {
    setData(d => {
      const diagPins = d.pins || [];
      const savedById = new Map((d.config.pins || []).map(p => [p.pin_id, p]));
      const next = diagPins.map(dp => {
        const saved = savedById.get(dp.pin_id) || {};
        const patch = patchFor(dp, saved) || {};
        return { ...saved, ...patch, pin_id: dp.pin_id, raw_id: dp.raw_id };
      });
      return { ...d, config: { ...d.config, pins: next } };
    });
    setDirty(true);
  }, []);

  if (err && !data) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-800 flex items-center gap-2">
        <AlertCircle size={14} /> {err}
      </div>
    );
  }
  if (!data) {
    return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading gutter builder…</div>;
  }

  return (
    <RateDetailProvider api={api} onSaved={() => { load(); refreshPreview(); }}>
      <GutterBuilderInner
        api={api}
        appraisalId={appraisalId}
        data={data}
        preview={preview}
        update={update}
        applyHeader={applyHeader}
        updatePin={updatePin}
        bulkUpdatePins={bulkUpdatePins}
        onSave={() => save(data.config)}
        onSaveWithPatch={(patch) => save({ ...data.config, ...patch })}
        onReload={load}
        busy={busy}
        err={err}
        dirty={dirty}
      />
    </RateDetailProvider>
  );
}

function GutterBuilderInner({ api, appraisalId, data, preview, update, applyHeader, updatePin, bulkUpdatePins, onSave, onSaveWithPatch, onReload, busy, err, dirty }) {
  const { config, pins, options } = data;
  const rateCtx = useRateDetail();

  const profileOptions = config.gutter_type === 'marley' ? options.marley_profiles : options.steel_profiles;
  const materialOptions = config.gutter_type === 'marley' ? [] : options.steel_materials;
  const currentMaterial = materialOptions.find(m => m.key === config.material_key);
  const needsColour = !!currentMaterial?.needs_colour || config.bracket_finish === 'colour';

  // Resolve the live rate row from preview stats so the operator can
  // see exactly what's driving the line totals.
  const gutterRow = preview?.stats?.gutter_row_name || '—';
  const bracketRow = preview?.stats?.bracket_row_name || '—';
  const totalLength = preview?.stats?.total_eff_length_m || 0;
  const totalBrackets = preview?.stats?.total_brackets || 0;
  const totalBracketsAfter = preview?.stats?.total_brackets_after_buffer || totalBrackets;
  const bracketGroups = preview?.stats?.bracket_groups || [];
  // Wave 3 M7 (2026-02) — surface pins with no matching rate row so
  // the operator sees under-charge risk BEFORE hitting Push (backend
  // also refuses to push with these outstanding).
  const unmatchedPins = preview?.stats?.unmatched_pins || [];

  // Resolve label maps for displaying per-pin profile/material.
  const profileLabelMap = useMemo(() => {
    const m = {};
    (options.steel_profiles || []).forEach(p => { m[p.key] = p.label; });
    (options.marley_profiles || []).forEach(p => { m[p.key] = p.label; });
    return m;
  }, [options]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4" data-testid="gutter-builder-panel">
      <div className="flex justify-end -mt-2 -mr-1">
        <SaveDocButton
          appraisalId={appraisalId}
          docKind="quotation_detailed"
          label="Save Quote PDF"
          expandChildren={false}
          variant="outline"
          size="sm"
          testId="gutter-save-quote-pdf"
        />
      </div>
      {/* Sprint S4 (2026-02c) · perimeter confidence banner. Reads the
          resolver bundle the backend puts on the config response so the
          appraiser sees where the aggregate gutter perimeter came from
          — pin sum, S3 on_perimeter pins, roof_details.perimeter_length,
          or nothing yet. Compact chip on the top-right so it doesn't
          steal focus from the pin table. */}
      {data.perimeter_resolver && (
        <div className="flex items-center justify-between gap-2 flex-wrap -mt-1" data-testid="gutter-perimeter-confidence">
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-2">
            <span>Perimeter</span>
            <span className="font-mono-num text-slate-800 normal-case tracking-normal font-semibold">
              {Number(data.perimeter_resolver.value || 0).toFixed(2)} m
            </span>
          </div>
          <ConfidenceBanner
            source={data.perimeter_resolver.source}
            confidence={data.perimeter_resolver.confidence}
            label="Gutter perimeter"
          />
        </div>
      )}
      {/* Wave 3 M7 (2026-02) — unmatched-pins banner. Backend refuses
          to push while this is non-empty because the pins would drop
          out of the quote silently. */}
      {unmatchedPins.length > 0 && (
        <div data-testid="gutter-unmatched-pins-banner" className="border-l-4 border-rose-500 bg-rose-50 rounded-md px-4 py-3 space-y-1">
          <div className="text-sm font-bold text-rose-900">
            ⚠ {unmatchedPins.length} pin{unmatchedPins.length === 1 ? '' : 's'} have no matching rate row
          </div>
          <div className="text-xs text-rose-800/80">
            These pins would be silently omitted from the quote (customer under-charged). Fix each pin's profile / material, or add the missing rate row before pushing to Components. Push is blocked while any pin is unresolved.
          </div>
          <ul className="mt-1 text-xs font-mono-num text-rose-900 pl-3 space-y-0.5">
            {unmatchedPins.slice(0, 6).map((p) => (
              <li key={p.raw_id || p.pin_id} data-testid={`gutter-unmatched-pin-${p.pin_id}`}>
                · pin <strong>{p.pin_id || p.raw_id?.slice(-6)}</strong> — profile <strong>{p.profile_key || '(blank)'}</strong> / material <strong>{p.material_key || '(blank)'}</strong> · {p.eff_length_m}m
              </li>
            ))}
            {unmatchedPins.length > 6 && (
              <li className="italic">… +{unmatchedPins.length - 6} more</li>
            )}
          </ul>
        </div>
      )}
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Droplets size={16} className="text-sky-700" />
          <h3 className="font-bold text-sm">Gutter Builder</h3>
          <label className="flex items-center gap-1.5 ml-3 text-xs">
            <input type="checkbox" checked={!!config.enabled} data-testid="gutter-enabled"
                   onChange={(e) => {
                     const v = e.target.checked;
                     update({ enabled: v });
                     // Iter130i — auto-save on enable/disable so it
                     // persists across reload without an extra click.
                     onSaveWithPatch({ enabled: v });
                   }} />
            <span className="font-semibold">Enabled</span>
          </label>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700">unsaved</span>}
          <button type="button" onClick={onReload} disabled={busy}
                  className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50">
            <RefreshCw size={11} className="inline mr-1" /> Reload
          </button>
          <button type="button" onClick={onSave} disabled={busy || !dirty}
                  data-testid="gutter-save"
                  className="px-3 py-1 text-xs bg-emerald-600 text-white rounded font-semibold disabled:opacity-50">
            <Save size={11} className="inline mr-1" /> Save
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-800 flex items-center gap-1">
          <AlertCircle size={12} /> {err}
        </div>
      )}

      {/* ── Top controls ── Iter129: compact (fixed widths), not grid. */}
      <div className="flex flex-wrap items-end gap-3 bg-slate-50 border border-slate-200 rounded p-3">
        <Field label="Gutter Type" width="9rem">
          <select value={config.gutter_type} disabled={busy} data-testid="gutter-type"
                  onChange={(e) => update({ gutter_type: e.target.value })}
                  className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
                  style={{ width: '9rem' }}>
            <option value="steel">Steel</option>
            <option value="marley">Marley (PVC)</option>
          </select>
        </Field>
        <Field label="Profile" width="11rem">
          <select value={config.profile_key} disabled={busy} data-testid="gutter-profile"
                  onChange={(e) => {
                    const valid = options.bracket_types_by_profile?.[e.target.value] || [];
                    const patch = { profile_key: e.target.value };
                    if (valid.length && !valid.includes(config.bracket_type)) patch.bracket_type = valid.includes('concealed') ? 'concealed' : valid[0];
                    applyHeader(patch, 'profile_key');
                  }}
                  className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
                  style={{ width: '11rem' }}>
            {profileOptions.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </Field>
        {config.gutter_type === 'steel' && (
          <Field label="Material" width="9rem">
            <select value={config.material_key} disabled={busy} data-testid="gutter-material"
                    onChange={(e) => applyHeader({ material_key: e.target.value }, 'material_key')}
                    className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
                    style={{ width: '9rem' }}>
              {materialOptions.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </Field>
        )}
        {/* Iter129 — Colour picker for painted materials (Maxam / MagnaFlow / MaxX / ZCore).
            Replaces the old Plain/Colour radio dropdown — the painted finish
            implicitly means a Colour choice, so we go straight to the swatch list. */}
        {currentMaterial?.needs_colour && (
          <Field label="Colour" width="11rem">
            <select value={config.colour_id || ''} disabled={busy} data-testid="gutter-colour"
                    onChange={(e) => update({
                      colour_id: e.target.value || null,
                      bracket_finish: e.target.value ? 'colour' : 'plain',
                    })}
                    className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
                    style={{ width: '11rem' }}>
              <option value="">— pick colour —</option>
              {(options.colours || []).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Bracket Type" width="11rem">
          <select value={config.bracket_type} disabled={busy} data-testid="gutter-bracket-type"
                  onChange={(e) => update({ bracket_type: e.target.value })}
                  className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
                  style={{ width: '11rem' }}>
            {options.bracket_types
              .filter(b => !(options.bracket_types_by_profile?.[config.profile_key]?.length)
                || options.bracket_types_by_profile[config.profile_key].includes(b.key))
              .map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        </Field>
        {/* Iter130e — Plain / Painted toggle has moved into the
            Brackets section below (avoids duplicate controls). */}
        <Field label="Bracket Span (mm)" width="8rem">
          <input type="number" step="50" min="100" max="1500"
                 value={Math.round((config.bracket_span_m || 0.7) * 1000)}
                 data-testid="gutter-default-span"
                 onChange={(e) => {
                   const mm = parseInt(e.target.value, 10);
                   const m = (Number.isFinite(mm) && mm > 0) ? mm / 1000 : 0.7;
                   update({ bracket_span_m: m });
                 }}
                 disabled={busy}
                 className="px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num"
                 style={{ width: '8rem' }} />
        </Field>
        <Field label="Mitre Extra (mm)" width="8rem">
          <select value={config.mitre_extra_mm} disabled={busy} data-testid="gutter-mitre-extra"
                  onChange={(e) => update({ mitre_extra_mm: parseInt(e.target.value, 10) })}
                  className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
                  style={{ width: '8rem' }}>
            {[150, 200, 250, 300, 350, 400, 500].map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </Field>
      </div>

      {/* ── Gutter length summary ─────────────────────────── */}
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs flex items-center justify-between">
        <div>
          <strong>Gutter lengths:</strong>{' '}
          {pins.length} run{pins.length !== 1 ? 's' : ''} · effective length{' '}
          <strong>{totalLength.toFixed(2)} m</strong> · {totalBrackets} brackets total
        </div>
        <div className="text-slate-600 text-[10px]">
          Gutter rate row: <span className="font-mono">{gutterRow}</span>
          {bracketRow !== '—' && <> · Bracket row: <span className="font-mono">{bracketRow}</span></>}
        </div>
      </div>

      {/* ── Disabled banner ─────────────────────────────── */}
      {!config.enabled && (
        <div className="bg-amber-50 border-l-4 border-amber-400 px-3 py-2 text-xs text-amber-800 font-semibold flex items-center justify-between">
          <span>⚠ The Gutter Builder is disabled. Tick <strong>Enabled</strong> above to compute Rate $/m, Line $ and bracket totals.</span>
          <button type="button"
                  data-testid="gutter-enable-cta"
                  onClick={() => { update({ enabled: true }); onSaveWithPatch({ enabled: true }); }}
                  disabled={busy}
                  className="ml-3 px-2 py-1 rounded bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700">
            Enable & save
          </button>
        </div>
      )}

      {/* ── Per-pin bulk actions ─────────────────────────── */}
      {pins.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Bulk:</span>
          <button type="button"
                  data-testid="gutter-bulk-override-all"
                  onClick={() => {
                    bulkUpdatePins((diag) => ({
                      length_override_m: Number(diag.length_m || 0).toFixed(2) * 1,
                    }));
                  }}
                  disabled={busy}
                  title="Copy each pin's Measured value into its Override field — ready to edit"
                  className="px-2 py-1 rounded border border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 font-semibold">
            ✎ Override all
          </button>
          <button type="button"
                  data-testid="gutter-bulk-undo-all"
                  onClick={() => {
                    bulkUpdatePins(() => ({ length_override_m: null }));
                  }}
                  disabled={busy}
                  title="Clear every pin's Override (m) — restore Measured values"
                  className="px-2 py-1 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 font-semibold">
            ⟲ Undo all overrides
          </button>
        </div>
      )}

      {/* ── Per-pin table ─────────────────────────────────── */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-slate-200" data-testid="gutter-pin-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left py-1.5 px-2">Pin</th>
              <th className="text-left py-1.5 px-2">Material</th>
              <th className="text-left py-1.5 px-2">Profile</th>
              <th className="text-right py-1.5 px-2">Measured (m)</th>
              <th className="text-right py-1.5 px-2">Override (m)</th>
              <th className="text-right py-1.5 px-2">Span (m)</th>
              <th className="text-right py-1.5 px-2">Brackets</th>
              <th className="text-left py-1.5 px-2">Outlets</th>
              <th className="text-center py-1.5 px-2 bg-violet-50/60">
                <div className="flex flex-col items-center gap-0.5">
                  <span>Mitres</span>
                  <button type="button"
                          data-testid="gutter-bulk-mitred-all"
                          onClick={() => {
                            const allMitred = pins.every(p => {
                              const s = (config.pins || []).find(x => x.pin_id === p.pin_id);
                              return !s?.stop_end_left && !s?.stop_end_right;
                            });
                            bulkUpdatePins(() => ({
                              stop_end_left: allMitred, stop_end_right: allMitred,
                              mitre_count: allMitred ? 0 : 2, mitred: !allMitred,
                            }));
                          }}
                          disabled={busy}
                          title="Set both ends of every run to Mitre (click again for Stop End both ends)"
                          className="text-[9px] leading-none px-1.5 py-0.5 rounded border border-violet-400 bg-white text-violet-700 hover:bg-violet-100 font-bold normal-case tracking-normal">
                    All mitres
                  </button>
                </div>
              </th>
              <th className="text-center py-1.5 px-2 bg-indigo-50/60" title="Every run has 2 ends — each is a Mitre or a Stop End">
                <span>Left end</span>
              </th>
              <th className="text-center py-1.5 px-2 bg-indigo-50/60">
                <span>Right end</span>
              </th>
              <th className="text-right py-1.5 px-2 bg-sky-50">Ordered (m)</th>
              <th className="text-right py-1.5 px-2">Mitre Extra</th>
              <th className="text-right py-1.5 px-2">Rate $/m</th>
              <th className="text-right py-1.5 px-2 bg-emerald-50">Line $</th>
            </tr>
          </thead>
          <tbody>
            {pins.map((pin) => {
              const saved = (config.pins || []).find(p => p.pin_id === pin.pin_id) || {};
              const previewLine = preview.lines.find(ln => ln.pin_id === pin.pin_id);
              return (
                <PinRow key={pin.raw_id || pin.pin_id}
                        pin={pin}
                        saved={saved}
                        line={previewLine}
                        defaultSpan={config.bracket_span_m}
                        defaultMitreMm={config.mitre_extra_mm}
                        defaultProfile={config.profile_key}
                        defaultMaterial={config.material_key}
                        profileOptions={profileOptions}
                        materialOptions={materialOptions}
                        onPatch={(patch, autoSave) => updatePin(pin.pin_id, pin.raw_id, patch, autoSave)}
                        busy={busy} />
              );
            })}
            {!pins.length && (
              <tr><td colSpan={15} className="text-center text-slate-400 py-4 italic">
                No Spouting pins on the diagram yet. Add Spouting pins under Diagram → Roof Plan.
              </td></tr>
            )}
          </tbody>
          {pins.length > 0 && (
            <tfoot className="bg-emerald-50 text-xs font-semibold">
              <tr data-testid="gutter-pins-subtotals">
                <td className="py-2 px-2" colSpan={3}>Subtotals</td>
                <td className="py-2 px-2 text-right font-mono-num" data-testid="gutter-measured-subtotal">
                  {pins.reduce((s, p) => s + (Number(p.length_m) || 0), 0).toFixed(2)} m
                </td>
                <td></td>
                <td></td>
                <td className="py-2 px-2 text-right font-mono-num" data-testid="gutter-brackets-qty-subtotal">
                  {preview.lines.filter(ln => ln.area === 'gutter' && ln.component_type && ln.component_type.startsWith('Gutter'))
                                .reduce((s, ln) => s + (ln.bracket_qty || 0), 0)} ea
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td className="py-2 px-2 text-right font-mono-num" data-testid="gutter-ordered-subtotal">
                  {preview.lines.filter(ln => ln.area === 'gutter' && ln.component_type && ln.component_type.startsWith('Gutter'))
                                .reduce((s, ln) => s + (ln.quantity || 0), 0).toFixed(2)} m
                </td>
                <td></td>
                <td></td>
                <td className="py-2 px-2 text-right" data-testid="gutter-pins-subtotal">
                  {fmt(preview.lines.filter(ln => ln.area === 'gutter' && ln.component_type !== 'Brackets')
                                    .reduce((s, ln) => s + (ln.total || 0), 0))}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ── Brackets table (iter130e — grouped by gutter profile) ─── */}
      <div className="bg-white border border-slate-200 rounded p-3 space-y-2" data-testid="gutter-brackets-section">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <h4 className="text-xs font-bold text-slate-700">Brackets</h4>
            <div className="text-[10px] text-slate-500">
              {totalBrackets} raw · {totalBracketsAfter} after buffer
            </div>
          </div>
          <Field label="Finish" width="8rem">
            <select value={config.bracket_finish} disabled={busy}
                    data-testid="gutter-bracket-finish-toggle"
                    onChange={(e) => update({ bracket_finish: e.target.value })}
                    className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
                    style={{ width: '8rem' }}>
              <option value="plain">Plain</option>
              <option value="colour">Painted</option>
            </select>
          </Field>
          <Field label="Buffer %" width="6rem">
            <select value={config.bracket_buffer_pct}
                    data-testid="gutter-bracket-buffer-pct"
                    onChange={(e) => update({ bracket_buffer_pct: parseFloat(e.target.value) })}
                    disabled={busy}
                    className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
                    style={{ width: '6rem' }}>
              <option value={0}>0%</option>
              <option value={5}>5%</option>
              <option value={10}>10%</option>
              <option value={15}>15%</option>
              <option value={20}>20%</option>
            </select>
          </Field>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs border border-slate-200" data-testid="gutter-brackets-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left  py-1.5 px-2">Gutter Profile</th>
                <th className="text-left  py-1.5 px-2">Bracket Type</th>
                <th className="text-right py-1.5 px-2">Bracket Qty</th>
                <th className="text-right py-1.5 px-2">Buffer Amt</th>
                <th className="text-right py-1.5 px-2">Total after Buffer</th>
                <th className="text-right py-1.5 px-2">Rate</th>
                <th className="text-right py-1.5 px-2 bg-emerald-50">Line $</th>
              </tr>
            </thead>
            <tbody>
              {bracketGroups.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-3 italic">
                  No brackets yet — enable the builder and add Spouting pins.
                </td></tr>
              )}
              {bracketGroups.map((g, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1 px-2 font-semibold text-slate-700">
                    {profileLabelMap[g.gutter_profile] || g.gutter_profile}
                  </td>
                  <td className="py-1 px-2 text-slate-700">{g.bracket_type_label || g.bracket_type}</td>
                  <td className="py-1 px-2 text-right font-mono-num">{g.bracket_qty}</td>
                  <td className="py-1 px-2 text-right font-mono-num text-amber-700">+{g.buffer_amt}</td>
                  <td className="py-1 px-2 text-right font-mono-num font-semibold">{g.qty_after_buffer}</td>
                  <td className="py-1 px-2 text-right font-mono-num">{fmt(g.rate)}</td>
                  <td className="py-1 px-2 text-right font-mono-num font-bold text-emerald-800 bg-emerald-50/40">{fmt(g.line_total)}</td>
                </tr>
              ))}
            </tbody>
            {bracketGroups.length > 0 && (
              <tfoot className="bg-emerald-50 text-xs font-semibold">
                <tr>
                  <td className="py-2 px-2" colSpan={6}>Brackets subtotal</td>
                  <td className="py-2 px-2 text-right font-mono-num" data-testid="gutter-brackets-subtotal">
                    {fmt(bracketGroups.reduce((s, g) => s + (g.line_total || 0), 0))}
                  </td>
                </tr>
                {(preview?.stats?.screws_qty || 0) > 0 && (
                  <tr data-testid="gutter-screws-row">
                    <td className="py-2 px-2" colSpan={2}>Bracket Screws (3 per bracket)</td>
                    <td className="py-2 px-2 text-right font-mono-num">{preview.stats.screws_qty}</td>
                    <td colSpan={2}></td>
                    <td className="py-2 px-2 text-right font-mono-num">{fmt(preview.stats.screws_rate)}</td>
                    <td className="py-2 px-2 text-right font-mono-num" data-testid="gutter-screws-total">
                      {fmt((preview.stats.screws_qty || 0) * (preview.stats.screws_rate || 0))}
                    </td>
                  </tr>
                )}
              </tfoot>
            )}
          </table>
        </div>

        {rateCtx && bracketGroups.length > 0 && preview?.stats?.bracket_groups?.[0] && (
          <button type="button"
                  data-testid="gutter-bracket-detail"
                  onClick={() => {
                    const g0 = bracketGroups[0];
                    const stub = {
                      id: 'bracket_active',
                      name: g0.bracket_name,
                      rate: g0.rate,
                      materials_rate: g0.rate * 0.7,
                      labour_rate: g0.rate * 0.3,
                    };
                    rateCtx.open(
                      config.bracket_type === 'finger_clip' ? 'fastening' : 'flashing',
                      stub,
                      {
                        label: `Bracket — ${g0.bracket_name}`,
                        detailOverride: config.bracket_detail_override || {},
                        onSaveOverride: async (body) => {
                          update({ bracket_detail_override: body });
                          setTimeout(() => onSave(), 100);
                        },
                      },
                    );
                  }}
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
            ✎ Edit bracket rate breakdown
          </button>
        )}
      </div>

      {/* ── Outlets / Droppers / Downpipes (iter130l) ─────── */}
      {pins.length > 0 && (
        <OutletsSection
          api={api}
          config={config}
          outletPins={data.outlets || []}
          stories={data.stories || 1}
          outletGroups={preview?.stats?.outlet_groups || []}
          previewLines={preview?.lines || []}
          update={update}
          onSaveWithPatch={onSaveWithPatch}
          busy={busy}
        />
      )}

      {/* ── Manual Entries (Phase 5 · pin-less quoting) ────
          Lets operators price gutter work over the phone before a
          site visit — no diagram pins required. Manual runs and
          outlets feed the same `_calc_lines` engine as diagram-
          derived ones, so the total on the confidence banner
          reflects both sources. Rendered ALWAYS so operators can
          layer manual entries on top of a partially-pinned
          diagram (start on the phone, refine after visit). */}
      <ManualEntriesSection
        config={config}
        pinsCount={pins.length}
        profileOptions={profileOptions}
        materialOptions={materialOptions}
        update={update}
        busy={busy}
      />

      {/* ── v10.29 Sprint 3 (2026-02c) · Stripping + Extra Hours ── */}
      <StrippingAndExtraHoursSection
        config={config}
        preview={preview}
        update={update}
        busy={busy}
      />

      {/* ── v10.29 Sprint 3 (2026-02c) · Consumables + Push summary ── */}
      <ConsumablesSummaryCard preview={preview} />
      <PushedLinesSummaryCard preview={preview} />
      {(preview?.lines || []).length > 0 && (
        <QuoteTotalStrip api={api} appraisalId={appraisalId} testid="gutter-quote-total-strip"
          linesTotal={(preview.lines || []).reduce((t, l) => t + (l.total || 0), 0)} />
      )}

      {/* ── Notes ─────────────────────────────────────────── */}
      <div className="text-xs">
        <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Notes</label>
        <textarea value={config.notes || ''} disabled={busy} rows={2}
                  onChange={(e) => update({ notes: e.target.value })}
                  data-testid="gutter-notes"
                  className="w-full px-2 py-1 mt-0.5 text-xs border border-slate-300 rounded" />
      </div>

      {/* ── Gutter rate Detail button (master material rate) ── */}
      {rateCtx && config.gutter_type === 'steel' && (
        <div className="flex items-center gap-2 text-[10px] text-slate-600">
          <span>Master gutter rate ({gutterRow}):</span>
          <button type="button"
                  data-testid="gutter-material-detail"
                  onClick={() => {
                    const stub = preview?.stats?.gutter_row_name ? {
                      id: 'gutter_active',
                      name: preview.stats.gutter_row_name,
                      rate: preview.stats.gutter_rate,
                      materials_rate: preview.stats.gutter_rate * 0.7,
                      labour_rate: preview.stats.gutter_rate * 0.3,
                    } : null;
                    if (!stub) return;
                    rateCtx.open('sheet', stub, {
                      label: `Gutter — ${preview.stats.gutter_row_name}`,
                      detailOverride: config.detail_override || {},
                      onSaveOverride: async (body) => {
                        update({ detail_override: body });
                        setTimeout(() => onSave(), 100);
                      },
                    });
                  }}
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
            ✎ Edit gutter breakdown
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, span = 2, width, children }) {
  // Iter129 — support fixed pixel/rem widths (`width="11rem"`) for compact
  // control rows; fall back to the legacy 12-col grid `span` prop.
  if (width) {
    return (
      <div className="flex flex-col gap-0.5" style={{ width }}>
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{label}</label>
        {children}
      </div>
    );
  }
  return (
    <div className={`col-span-12 sm:col-span-${span} flex flex-col gap-0.5`}>
      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function PinRow({ pin, saved, line, defaultSpan, defaultMitreMm, defaultProfile, defaultMaterial, profileOptions, materialOptions, onPatch, busy }) {
  const measured = pin.length_m || 0;
  const overrideVal = saved.length_override_m;
  const inheritLen = overrideVal === undefined || overrideVal === null || overrideVal === '';
  const span = saved.bracket_span_m ?? defaultSpan;
  // Ends rule: 2 ends per run, each Mitre or Stop End → mitres = 2 − stop ends.
  const stopL = !!saved.stop_end_left;
  const stopR = !!saved.stop_end_right;
  const mitreCount = 2 - (stopL ? 1 : 0) - (stopR ? 1 : 0);
  const mitred = mitreCount > 0;
  const setEnd = (side, kind) => {
    const nextL = side === 'left' ? kind === 'stop' : stopL;
    const nextR = side === 'right' ? kind === 'stop' : stopR;
    const mc = 2 - (nextL ? 1 : 0) - (nextR ? 1 : 0);
    onPatch({ stop_end_left: nextL, stop_end_right: nextR, mitre_count: mc, mitred: mc > 0 }, true);
  };
  const mitreExtra = saved.mitre_extra_mm ?? defaultMitreMm;

  // Iter130 — Ordered (m) column. Editable; if blank, falls back to
  // auto-calc shown as a placeholder so the operator sees what would
  // be ordered without typing anything. Auto = (override or measured)
  // + (mitre_count × mitre_extra / 1000) — each mitred end adds one Mitre Extra.
  const baseLen = inheritLen ? measured : (parseFloat(overrideVal) || 0);
  const mitreTotalM = (mitreCount * mitreExtra) / 1000;
  const autoOrdered = baseLen + mitreTotalM;
  const orderedVal = saved.ordered_length_m;
  const inheritOrdered = orderedVal === undefined || orderedVal === null || orderedVal === '';

  // Iter130e — per-pin profile / material override.
  const pinProfile  = saved.profile_key  || '';
  const pinMaterial = saved.material_key || '';
  const outletList  = (pin.outlets || []).join(', ');

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60">
      <td className="py-1 px-2 font-semibold text-slate-700">
        <MapPin size={10} className="inline mr-1 text-sky-600" />
        {pin.pin_id}
      </td>
      <td className="py-1 px-2">
        <select value={pinMaterial}
                data-testid={`gutter-pin-material-${pin.pin_id}`}
                onChange={(e) => onPatch({ material_key: e.target.value || null })}
                disabled={busy || (materialOptions || []).length === 0}
                title={`Default: ${defaultMaterial || '—'}`}
                className="px-1 py-0.5 text-[10px] border border-slate-300 rounded bg-white">
          <option value="">{(materialOptions || []).find(m => m.key === defaultMaterial)?.label || defaultMaterial}</option>
          {(materialOptions || []).map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </td>
      <td className="py-1 px-2">
        <select value={pinProfile}
                data-testid={`gutter-pin-profile-${pin.pin_id}`}
                onChange={(e) => onPatch({ profile_key: e.target.value || null })}
                disabled={busy}
                title={`Default: ${defaultProfile}`}
                className="px-1 py-0.5 text-[10px] border border-slate-300 rounded bg-white">
          <option value="">{(profileOptions || []).find(p => p.key === defaultProfile)?.label || defaultProfile}</option>
          {(profileOptions || []).map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </td>
      <td className="py-1 px-2 text-right font-mono-num text-slate-500">{measured.toFixed(2)}</td>
      <td className="py-1 px-2 text-right">
        <div className="inline-flex items-center gap-1">
          <input type="number" step="0.1" min="0"
                 value={inheritLen ? '' : overrideVal}
                 placeholder={measured.toFixed(2)}
                 data-testid={`gutter-pin-override-${pin.pin_id}`}
                 onChange={(e) => onPatch({ length_override_m: e.target.value === '' ? null : parseFloat(e.target.value) })}
                 disabled={busy}
                 className="w-16 px-1 py-0.5 text-xs border border-slate-300 rounded text-right font-mono-num" />
          {!inheritLen && (
            <button type="button"
                    data-testid={`gutter-pin-override-undo-${pin.pin_id}`}
                    onClick={() => onPatch({ length_override_m: null })}
                    disabled={busy}
                    title={`Undo override — restore measured ${measured.toFixed(2)} m`}
                    className="text-[11px] leading-none w-5 h-5 rounded border border-slate-300 bg-white hover:bg-slate-100 text-slate-600 disabled:opacity-50">
              ⟲
            </button>
          )}
        </div>
      </td>
      <td className="py-1 px-2 text-right">
        <input type="number" step="0.1" min="0.1" max="3"
               value={span}
               data-testid={`gutter-pin-span-${pin.pin_id}`}
               onChange={(e) => onPatch({ bracket_span_m: parseFloat(e.target.value) || defaultSpan })}
               disabled={busy}
               className="w-14 px-1 py-0.5 text-xs border border-slate-300 rounded text-right font-mono-num" />
      </td>
      <td className="py-1 px-2 text-right bg-amber-50/40">
        <input type="number" step="1" min="0" max="999"
               value={saved.bracket_count_override ?? ''}
               placeholder={String(line?.bracket_qty ?? 0)}
               data-testid={`gutter-pin-brackets-${pin.pin_id}`}
               onChange={(e) => onPatch({ bracket_count_override: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
               disabled={busy}
               title={`Auto = ceil(${(line?.quantity ?? 0).toFixed(2)} / ${span}) + 1 = ${line?.bracket_qty ?? 0}. Type to override.`}
               className="w-14 px-1 py-0.5 text-xs border border-amber-300 rounded text-right font-mono-num bg-white" />
      </td>
      <td className="py-1 px-2 text-xs text-amber-700 font-semibold" data-testid={`gutter-pin-outlets-${pin.pin_id}`}>
        {outletList || <span className="text-slate-300 font-normal">—</span>}
      </td>
      <td className="py-1 px-2 text-center bg-violet-50/30">
        <span data-testid={`gutter-pin-mitre-count-${pin.pin_id}`}
              title="Mitres = ends set to Mitre (each adds the Mitre Extra length)"
              className="font-mono-num text-[11px] font-bold text-violet-800">{mitreCount}</span>
      </td>
      {[['left', stopL], ['right', stopR]].map(([side, isStop]) => (
        <td key={side} className="py-1 px-2 text-center bg-indigo-50/30">
          <select value={isStop ? 'stop' : 'mitre'}
                  data-testid={`gutter-pin-end-${side}-${pin.pin_id}`}
                  onChange={(e) => setEnd(side, e.target.value)}
                  disabled={busy}
                  title={`${side === 'left' ? 'Left' : 'Right'} end — Mitre (joins another run) or Stop End (open end)`}
                  className="px-1 py-0.5 text-[11px] border border-indigo-300 rounded bg-white">
            <option value="mitre">Mitre</option>
            <option value="stop">Stop End</option>
          </select>
        </td>
      ))}
      <td className="py-1 px-2 text-right bg-sky-50/40">
        <input type="number" step="0.01" min="0"
               value={inheritOrdered ? '' : orderedVal}
               placeholder={autoOrdered.toFixed(2)}
               data-testid={`gutter-pin-ordered-${pin.pin_id}`}
               onChange={(e) => onPatch({ ordered_length_m: e.target.value === '' ? null : parseFloat(e.target.value) })}
               disabled={busy}
               title={mitred ? `Auto = ${baseLen.toFixed(2)} + ${mitreCount}×${mitreExtra}mm = ${autoOrdered.toFixed(2)} m` : `Auto = ${baseLen.toFixed(2)} m`}
               className="w-20 px-1 py-0.5 text-xs border border-sky-300 rounded text-right font-mono-num bg-white" />
      </td>
      <td className="py-1 px-2 text-right">
        {mitred ? (
          <select value={mitreExtra}
                  data-testid={`gutter-pin-mitre-extra-${pin.pin_id}`}
                  onChange={(e) => onPatch({ mitre_extra_mm: parseInt(e.target.value, 10) })}
                  disabled={busy}
                  className="px-1 py-0.5 text-xs border border-slate-300 rounded">
            {[150, 200, 250, 300, 350, 400, 500].map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="py-1 px-2 text-right">
        <div className="flex flex-col items-end gap-0.5">
          <span className="font-mono-num text-slate-700" data-testid={`gutter-pin-rate-${pin.pin_id}`}>
            {line ? fmt(line.rate) : '—'}
          </span>
          {line?.type_name && (
            <span className="text-[9px] text-slate-400 leading-tight max-w-[10rem] truncate"
                  title={line.type_name}
                  data-testid={`gutter-pin-rate-row-${pin.pin_id}`}>
              {line.type_name}
            </span>
          )}
        </div>
      </td>
      <td className="py-1 px-2 text-right font-mono-num font-bold text-emerald-800 bg-emerald-50/40">
        {line ? fmt(line.total) : fmt(0)}
      </td>
    </tr>
  );
}



// Iter130l — Outlets / Droppers / Downpipes / Bends / DP Brackets section.
function OutletsSection({ api, config, outletPins, stories, outletGroups, previewLines, update, onSaveWithPatch, busy }) {
  const overrides   = config.outlet_overrides || [];
  const ovByRaw     = useMemo(() => {
    const m = {};
    overrides.forEach(o => { if (o.raw_id) m[o.raw_id] = o; });
    return m;
  }, [overrides]);

  // Iter130q — Rainhead Type catalogue (loaded once per mount).
  const [rainheadTypes, setRainheadTypes] = useState([]);
  const [showAddType, setShowAddType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeRate, setNewTypeRate] = useState('');
  const [addTypeErr, setAddTypeErr] = useState('');

  const loadRainheadTypes = useCallback(async () => {
    try {
      const { data } = await api.get('/guttering/rainhead-types');
      setRainheadTypes(data.types || []);
    } catch (e) { /* network — leave empty */ }
  }, [api]);
  useEffect(() => { loadRainheadTypes(); }, [loadRainheadTypes]);

  const saveNewType = async () => {
    setAddTypeErr('');
    if (!newTypeName.trim() || !newTypeRate) { setAddTypeErr('Name and rate required'); return; }
    try {
      await api.post('/guttering/rainhead-types', {
        name: newTypeName.trim(),
        rate: parseFloat(newTypeRate) || 0,
      });
      await loadRainheadTypes();
      setShowAddType(false);
      setNewTypeName('');
      setNewTypeRate('');
    } catch (e) {
      setAddTypeErr(e?.response?.data?.detail || String(e));
    }
  };

  const patchOutletOverride = (raw_id, ref, patch, autoSave = false) => {
    const next = [...overrides];
    const idx = next.findIndex(o => o.raw_id === raw_id);
    const base = idx >= 0 ? next[idx] : { raw_id, ref, dp_action: config.default_dp_action || 'leave', bend_count: 2 };
    const merged = { ...base, ...patch, raw_id, ref };
    if (idx >= 0) next[idx] = merged; else next.push(merged);
    update({ outlet_overrides: next });
    if (autoSave) onSaveWithPatch({ outlet_overrides: next });
  };

  if (!outletPins.length) {
    return (
      <div className="bg-white border border-slate-200 rounded p-3" data-testid="gutter-outlets-section-empty">
        <h4 className="text-xs font-bold text-slate-700 mb-1">Outlets / Droppers / Downpipes</h4>
        <div className="text-[11px] text-slate-400 italic">
          No Outlet pins on the diagram yet. Drop Outlet pins on the Roof Plan to add Droppers + Downpipes here.
        </div>
      </div>
    );
  }

  const droppersTotal   = previewLines.filter(ln => ln.component_type === 'Dropper').reduce((s, ln) => s + (ln.total || 0), 0);
  const dpTotal         = previewLines.filter(ln => ln.component_type === 'Downpipe').reduce((s, ln) => s + (ln.total || 0), 0);
  const bendsTotal      = previewLines.filter(ln => ln.component_type === 'Downpipe Bend').reduce((s, ln) => s + (ln.total || 0), 0);
  const dpBracketsTotal = previewLines.filter(ln => ln.component_type === 'Downpipe Bracket').reduce((s, ln) => s + (ln.total || 0), 0);
  const installsTotal   = previewLines.filter(ln => ln.component_type === 'Dropper Install').reduce((s, ln) => s + (ln.total || 0), 0);
  const rainheadsTotal  = previewLines.filter(ln => ln.component_type === 'Rainhead').reduce((s, ln) => s + (ln.total || 0), 0);
  const sectionTotal    = droppersTotal + dpTotal + bendsTotal + dpBracketsTotal + installsTotal + rainheadsTotal;

  return (
    <div className="bg-white border border-slate-200 rounded p-3 space-y-2" data-testid="gutter-outlets-section">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h4 className="text-xs font-bold text-slate-700">Outlets / Droppers / Downpipes</h4>
          <div className="text-[10px] text-slate-500">
            {outletPins.length} outlet{outletPins.length !== 1 ? 's' : ''} · stories: {stories} · default surfeit {config.surfeit_default_mm}mm
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Default surfeit (mm)" width="7rem">
            <select value={config.surfeit_default_mm}
                    data-testid="gutter-surfeit-default"
                    onChange={(e) => update({ surfeit_default_mm: parseInt(e.target.value, 10) })}
                    disabled={busy}
                    className="px-2 py-1 text-xs border border-slate-300 rounded bg-white font-mono-num"
                    style={{ width: '7rem' }}>
              {Array.from({ length: 21 }, (_, i) => 200 + i * 50).map(v =>
                <option key={v} value={v}>{v} mm</option>
              )}
            </select>
          </Field>
          <Field label="Default bend" width="6rem">
            <select value={config.default_bend_angle} data-testid="gutter-default-bend-angle"
                    onChange={(e) => update({ default_bend_angle: e.target.value })}
                    disabled={busy}
                    className="px-2 py-1 text-xs border border-slate-300 rounded bg-white" style={{ width: '6rem' }}>
              <option value="65">65°</option>
              <option value="95">95°</option>
            </select>
          </Field>
          <Field label="Default action" width="7rem">
            <select value={config.default_dp_action} data-testid="gutter-default-dp-action"
                    onChange={(e) => update({ default_dp_action: e.target.value })}
                    disabled={busy}
                    className="px-2 py-1 text-xs border border-slate-300 rounded bg-white" style={{ width: '7rem' }}>
              <option value="leave">Leave</option>
              <option value="replace">Replace</option>
            </select>
          </Field>
          <Field label="Downpipe" width="8rem">
            <select value={config.downpipe_material || 'pvc'} data-testid="gutter-downpipe-material"
                    onChange={(e) => update({ downpipe_material: e.target.value })}
                    disabled={busy} title="Default for every outlet — each outlet can be changed below"
                    className="px-2 py-1 text-xs border border-slate-300 rounded bg-white" style={{ width: '8rem' }}>
              <option value="pvc">PVC (Marley)</option>
              <option value="steel">Steel</option>
            </select>
          </Field>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-slate-200" data-testid="gutter-outlets-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left  py-1.5 px-2">Outlet</th>
              <th className="text-left  py-1.5 px-2">Size</th>
              <th className="text-left  py-1.5 px-2">Dropper</th>
              <th className="text-left  py-1.5 px-2">Downpipe</th>
              <th className="text-center py-1.5 px-2">Action</th>
              <th className="text-right py-1.5 px-2">Surfeit (mm)</th>
              <th className="text-right py-1.5 px-2">Stories / Drop (m)</th>
              <th className="text-right py-1.5 px-2">DP Length (m)</th>
              <th className="text-center py-1.5 px-2">Bend</th>
              <th className="text-right py-1.5 px-2">DP Brackets</th>
              <th className="text-center py-1.5 px-2 bg-rose-50">Rainhead</th>
              <th className="text-right py-1.5 px-2 bg-emerald-50">Line $</th>
            </tr>
          </thead>
          <tbody>
            {outletPins.map((o) => {
              const ov = ovByRaw[o.raw_id] || {};
              const grp = outletGroups.find(g => g.raw_id === o.raw_id) || {};
              const action = ov.dp_action || grp.dp_action || config.default_dp_action;
              const surfeit = ov.surfeit_mm ?? config.surfeit_default_mm;
              const storiesOv = ov.stories_override ?? stories;
              const drop = grp.height_m ?? (storiesOv >= 2 ? config.double_story_height_m : config.single_story_height_m);
              const dpLen = grp.dp_length_m ?? ((surfeit / 1000) + drop);
              const bendCount = ov.bend_count ?? 2;
              const bendAngle = ov.bend_angle || config.default_bend_angle;
              // Iter130m — DP brackets are 2 per 3m of DROP only
              // (excluding the horizontal surfeit length).
              const dpBrackets = grp.dp_brackets ?? Math.ceil(drop * (2/3));
              // Per-outlet line $ (sum dropper + DP + bends + DP brackets for this outlet ref).
              const lineSum = previewLines
                .filter(ln => ln.outlet_ref === o.ref)
                .reduce((s, ln) => s + (ln.total || 0), 0);
              return (
                <React.Fragment key={o.raw_id}>
                <tr className="border-t border-slate-100">
                  <td className="py-1 px-2 font-semibold text-amber-700">{o.ref}</td>
                  <td className="py-1 px-2 text-slate-700">{o.diameter_mm}mm {o.shape}</td>
                  <td className="py-1 px-2 text-slate-700">{grp.dropper_size || '—'}</td>
                  <td className="py-1 px-2 text-slate-700">{grp.dp_size || '—'}</td>
                  <td className="py-1 px-2 text-center">
                    <select value={action} data-testid={`gutter-outlet-action-${o.ref}`}
                            onChange={(e) => patchOutletOverride(o.raw_id, o.ref, { dp_action: e.target.value }, true)}
                            disabled={busy}
                            className={`px-1 py-0.5 text-[10px] border rounded ${action === 'replace' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-slate-50 text-slate-600 border-slate-300'}`}>
                      <option value="leave">Leave</option>
                      <option value="replace">Replace</option>
                    </select>
                    {action === 'replace' && (
                      <select value={ov.dp_material || ''} data-testid={`gutter-outlet-dp-material-${o.ref}`}
                              onChange={(e) => patchOutletOverride(o.raw_id, o.ref, { dp_material: e.target.value || null }, true)}
                              disabled={busy} title="Downpipe material for this outlet"
                              className="ml-1 px-1 py-0.5 text-[10px] border border-slate-300 rounded bg-white">
                        <option value="">{(config.downpipe_material || 'pvc') === 'steel' ? 'Steel (default)' : 'PVC (default)'}</option>
                        <option value="pvc">PVC (Marley)</option>
                        <option value="steel">Steel</option>
                      </select>
                    )}
                  </td>
                  <td className="py-1 px-2 text-right">
                    <select value={surfeit}
                           data-testid={`gutter-outlet-surfeit-${o.ref}`}
                           onChange={(e) => patchOutletOverride(o.raw_id, o.ref, { surfeit_mm: parseInt(e.target.value, 10) }, true)}
                           disabled={busy}
                           className="px-1 py-0.5 text-[10px] border border-slate-300 rounded bg-white font-mono-num">
                      {Array.from({ length: 21 }, (_, i) => 200 + i * 50).map(v =>
                        <option key={v} value={v}>{v}</option>
                      )}
                    </select>
                  </td>
                  <td className="py-1 px-2 text-right">
                    <select value={storiesOv} data-testid={`gutter-outlet-stories-${o.ref}`}
                            onChange={(e) => patchOutletOverride(o.raw_id, o.ref, { stories_override: parseInt(e.target.value, 10) }, true)}
                            disabled={busy}
                            className="px-1 py-0.5 text-[10px] border border-slate-300 rounded bg-white">
                      <option value={1}>1 ({config.single_story_height_m}m)</option>
                      <option value={2}>2 ({config.double_story_height_m}m)</option>
                    </select>
                  </td>
                  <td className="py-1 px-2 text-right font-mono-num font-semibold text-slate-700">{dpLen.toFixed(2)}</td>
                  <td className="py-1 px-2 text-center">
                    <select value={bendAngle} data-testid={`gutter-outlet-bend-${o.ref}`}
                            onChange={(e) => patchOutletOverride(o.raw_id, o.ref, { bend_angle: e.target.value }, true)}
                            disabled={busy}
                            className="px-1 py-0.5 text-[10px] border border-slate-300 rounded bg-white">
                      <option value="65">{bendCount}× 65°</option>
                      <option value="95">{bendCount}× 95°</option>
                    </select>
                  </td>
                  <td className="py-1 px-2 text-right font-mono-num text-slate-700">{dpBrackets}</td>
                  <td className="py-1 px-2 text-center bg-rose-50/40">
                    <input type="checkbox" checked={!!ov.rainhead_enabled}
                           data-testid={`gutter-outlet-rainhead-${o.ref}`}
                           onChange={(e) => patchOutletOverride(o.raw_id, o.ref, { rainhead_enabled: e.target.checked }, true)}
                           disabled={busy} />
                  </td>
                  <td className="py-1 px-2 text-right font-mono-num font-bold text-emerald-800 bg-emerald-50/40">{fmt(lineSum)}</td>
                </tr>
                {ov.rainhead_enabled && (
                  <tr key={o.raw_id + '_rh'} className="bg-rose-50/30 border-t border-rose-200" data-testid={`gutter-outlet-rainhead-row-${o.ref}`}>
                    <td colSpan={12} className="py-2 px-3">
                      <div className="flex flex-wrap items-end gap-3 text-[11px]">
                        <div className="flex flex-col">
                          <span className="font-bold text-rose-700">Rainhead box (optional) — {o.ref}</span>
                          <span className="text-[10px] text-slate-500">Pick a type, or override the rate manually</span>
                        </div>
                        <label className="flex flex-col min-w-[14rem]">
                          <span className="text-[9px] uppercase font-semibold text-slate-500">Rainhead Type</span>
                          <div className="flex items-stretch gap-1">
                            <select value={ov.rainhead_type_id || ''}
                                    data-testid={`gutter-rh-type-${o.ref}`}
                                    onChange={(e) => {
                                      const id = e.target.value;
                                      const t = rainheadTypes.find(x => x.id === id);
                                      patchOutletOverride(o.raw_id, o.ref, {
                                        rainhead_type_id: id || null,
                                        // Auto-fill rate from the picked type.
                                        ...(t ? { rainhead_rate: t.rate } : {}),
                                      }, true);
                                    }}
                                    disabled={busy}
                                    className="flex-1 px-1 py-0.5 text-xs border border-rose-300 rounded bg-white">
                              <option value="">— Manual entry —</option>
                              {rainheadTypes.map(t => (
                                <option key={t.id} value={t.id}>{t.name} — ${t.rate.toFixed(2)}</option>
                              ))}
                            </select>
                            <button type="button"
                                    data-testid={`gutter-rh-add-type-${o.ref}`}
                                    onClick={() => setShowAddType(s => !s)}
                                    title="Add a new Rainhead Type to the catalogue"
                                    className="px-1.5 text-[12px] leading-none rounded border border-rose-400 bg-white text-rose-700 hover:bg-rose-100">
                              {showAddType ? '×' : '+'}
                            </button>
                          </div>
                        </label>
                        <label className="flex flex-col">
                          <span className="text-[9px] uppercase font-semibold text-slate-500">QTY (ea)</span>
                          <input type="number" step="1" min="0" value={ov.rainhead_qty ?? 1}
                                 data-testid={`gutter-rh-qty-${o.ref}`}
                                 onChange={(e) => patchOutletOverride(o.raw_id, o.ref, { rainhead_qty: parseFloat(e.target.value) || 0 })}
                                 onBlur={() => onSaveWithPatch({ outlet_overrides: overrides })}
                                 className="w-20 px-1 py-0.5 text-xs border border-rose-300 rounded text-right font-mono-num bg-white" />
                        </label>
                        <label className="flex flex-col">
                          <span className="text-[9px] uppercase font-semibold text-slate-500">RATE ($/ea)</span>
                          <input type="number" step="5" min="0" value={ov.rainhead_rate ?? 245}
                                 data-testid={`gutter-rh-rate-${o.ref}`}
                                 onChange={(e) => patchOutletOverride(o.raw_id, o.ref, { rainhead_rate: parseFloat(e.target.value) || 0 })}
                                 onBlur={() => onSaveWithPatch({ outlet_overrides: overrides })}
                                 className="w-24 px-1 py-0.5 text-xs border border-rose-300 rounded text-right font-mono-num bg-white" />
                        </label>
                        <label className="flex flex-col flex-1 min-w-[10rem]">
                          <span className="text-[9px] uppercase font-semibold text-slate-500">Notes / Dimensions</span>
                          <input type="text" value={ov.rainhead_notes ?? ''}
                                 data-testid={`gutter-rh-notes-${o.ref}`}
                                 onChange={(e) => patchOutletOverride(o.raw_id, o.ref, { rainhead_notes: e.target.value })}
                                 onBlur={() => onSaveWithPatch({ outlet_overrides: overrides })}
                                 placeholder="e.g. 400×500 colour-matched"
                                 className="px-1 py-0.5 text-xs border border-rose-300 rounded bg-white" />
                        </label>
                        <div className="text-right">
                          <div className="text-[9px] uppercase font-semibold text-slate-500">LINE TOTAL</div>
                          <div className="font-mono-num font-bold text-emerald-800 text-sm" data-testid={`gutter-rh-total-${o.ref}`}>
                            {fmt((ov.rainhead_qty ?? 1) * (ov.rainhead_rate ?? 245))}
                          </div>
                        </div>
                      </div>

                      {/* Inline "Add new Rainhead Type" form */}
                      {showAddType && (
                        <div className="mt-2 p-2 border border-rose-300 rounded bg-rose-100/40 flex flex-wrap items-end gap-2"
                             data-testid="gutter-rh-add-type-form">
                          <label className="flex flex-col flex-1 min-w-[12rem]">
                            <span className="text-[9px] uppercase font-semibold text-slate-500">New Type Name</span>
                            <input type="text" value={newTypeName}
                                   data-testid="gutter-rh-new-type-name"
                                   onChange={(e) => setNewTypeName(e.target.value)}
                                   placeholder="e.g. Rainhead — Coastal 350"
                                   className="px-1 py-0.5 text-xs border border-rose-300 rounded bg-white" />
                          </label>
                          <label className="flex flex-col">
                            <span className="text-[9px] uppercase font-semibold text-slate-500">Rate ($/ea)</span>
                            <input type="number" step="5" min="0" value={newTypeRate}
                                   data-testid="gutter-rh-new-type-rate"
                                   onChange={(e) => setNewTypeRate(e.target.value)}
                                   placeholder="245"
                                   className="w-24 px-1 py-0.5 text-xs border border-rose-300 rounded text-right font-mono-num bg-white" />
                          </label>
                          <button type="button"
                                  data-testid="gutter-rh-new-type-save"
                                  onClick={saveNewType}
                                  className="px-2 py-1 rounded bg-rose-600 text-white text-[11px] font-bold hover:bg-rose-700">
                            Save Type
                          </button>
                          {addTypeErr && <span className="text-[10px] text-rose-700">{addTypeErr}</span>}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-emerald-50 text-xs font-semibold">
            <tr>
              <td className="py-2 px-2" colSpan={11}>Outlets subtotal (droppers + downpipes + bends + DP brackets + rainheads)</td>
              <td className="py-2 px-2 text-right font-mono-num" data-testid="gutter-outlets-subtotal">
                {fmt(sectionTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="text-[10px] text-slate-500 italic">
        Downpipe length = (surfeit ÷ 1000) + drop. DP brackets = 2 per 3m of <strong>drop only</strong> (surfeit excluded). Bends = 2 per outlet at the chosen angle.
        80mm Round outlets pull pricing from the Marley RP80 catalogue.
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Manual Entries · Phase 5 pin-less quoting (2026-02c)
// ─────────────────────────────────────────────────────────────────
/**
 * Renders two editable tables:
 *   1. Manual gutter runs — length_m + mitre_count + optional
 *      per-run profile/material override.
 *   2. Manual outlets/downpipes — diameter_mm + shape + qty.
 *
 * Both are persisted through the existing `update({...})` handler,
 * saved on the shared "Save Config" button, and consumed by the
 * backend's `_synthesize_manual_pins_and_outlets` helper.
 *
 * Empty-state callout appears when the diagram has zero Spouting/
 * Outlet pins so operators immediately see the pin-less path.
 */
function ManualEntriesSection({ config, pinsCount, profileOptions, materialOptions, update, busy }) {
  const manualPins    = config.manual_pins    || [];
  const manualOutlets = config.manual_outlets || [];
  const hasDiagramPins = pinsCount > 0;

  const addManualPin = () => {
    update({
      manual_pins: [...manualPins, { length_m: 0, mitre_count: 0, profile_key: '', material_key: '', label: '' }],
    });
  };
  const updateManualPin = (idx, patch) => {
    const next = manualPins.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    update({ manual_pins: next });
  };
  const removeManualPin = (idx) => {
    update({ manual_pins: manualPins.filter((_, i) => i !== idx) });
  };

  const addManualOutlet = () => {
    update({
      manual_outlets: [...manualOutlets, { diameter_mm: 80, shape: 'Round', qty: 1, label: '' }],
    });
  };
  const updateManualOutlet = (idx, patch) => {
    const next = manualOutlets.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    update({ manual_outlets: next });
  };
  const removeManualOutlet = (idx) => {
    update({ manual_outlets: manualOutlets.filter((_, i) => i !== idx) });
  };

  const totalManualLength = manualPins.reduce((s, p) => s + (Number(p.length_m) || 0), 0);
  const totalManualOutlets = manualOutlets.reduce((s, o) => s + (Number(o.qty) || 1), 0);
  const hasAnyManual = manualPins.length > 0 || manualOutlets.length > 0;

  return (
    <div
      data-testid="gutter-manual-entries"
      className="border border-dashed border-amber-300 bg-amber-50/40 rounded-md p-3 space-y-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-amber-800">
            Manual Entries · pin-less quoting
          </h3>
          <p className="text-[11px] text-amber-700 mt-0.5">
            {hasDiagramPins
              ? 'Add extra runs or outlets that aren\'t on the diagram (e.g. phone quote add-ons refined later).'
              : 'No diagram pins yet? Add manual runs + outlets here to build a phone quote before the site visit.'}
          </p>
        </div>
        {hasAnyManual && (
          <span
            data-testid="gutter-manual-summary"
            className="text-[10px] font-mono px-2 py-1 bg-amber-100 text-amber-900 rounded border border-amber-200 whitespace-nowrap"
          >
            {totalManualLength.toFixed(1)}m · {totalManualOutlets} outlet{totalManualOutlets === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* Manual runs */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Runs</span>
          <button
            type="button"
            data-testid="gutter-manual-pin-add"
            onClick={addManualPin}
            disabled={busy}
            className="text-[10px] font-semibold px-2 py-0.5 rounded border border-amber-400 bg-white text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          >
            + Add run
          </button>
        </div>
        {manualPins.length === 0 ? (
          <p className="text-[10px] italic text-slate-500 px-2 py-1">No manual runs yet.</p>
        ) : (
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-slate-500">
                <th className="text-left py-1 px-1 font-semibold w-32">Label</th>
                <th className="text-right py-1 px-1 font-semibold w-24">Length (m)</th>
                <th className="text-right py-1 px-1 font-semibold w-20">Mitres</th>
                <th className="text-left py-1 px-1 font-semibold">Profile override</th>
                <th className="text-left py-1 px-1 font-semibold">Material override</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {manualPins.map((p, idx) => (
                <tr key={idx} className="border-t border-amber-100" data-testid={`gutter-manual-pin-row-${idx}`}>
                  <td className="py-1 px-1">
                    <input
                      type="text"
                      value={p.label || ''}
                      onChange={(e) => updateManualPin(idx, { label: e.target.value })}
                      disabled={busy}
                      placeholder={`Run ${idx + 1}`}
                      data-testid={`gutter-manual-pin-label-${idx}`}
                      className="w-full px-1.5 py-0.5 text-[11px] border border-slate-300 rounded bg-white"
                    />
                  </td>
                  <td className="py-1 px-1">
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={p.length_m ?? ''}
                      onChange={(e) => updateManualPin(idx, { length_m: e.target.value === '' ? 0 : Number(e.target.value) })}
                      disabled={busy}
                      data-testid={`gutter-manual-pin-length-${idx}`}
                      className="w-full px-1.5 py-0.5 text-[11px] border border-slate-300 rounded text-right font-mono-num"
                    />
                  </td>
                  <td className="py-1 px-1">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={p.mitre_count ?? 0}
                      onChange={(e) => updateManualPin(idx, { mitre_count: e.target.value === '' ? 0 : Number(e.target.value) })}
                      disabled={busy}
                      data-testid={`gutter-manual-pin-mitres-${idx}`}
                      className="w-full px-1.5 py-0.5 text-[11px] border border-slate-300 rounded text-right font-mono-num"
                    />
                  </td>
                  <td className="py-1 px-1">
                    <select
                      value={p.profile_key || ''}
                      onChange={(e) => updateManualPin(idx, { profile_key: e.target.value })}
                      disabled={busy}
                      data-testid={`gutter-manual-pin-profile-${idx}`}
                      className="w-full px-1.5 py-0.5 text-[11px] border border-slate-300 rounded bg-white"
                    >
                      <option value="">— use top-level —</option>
                      {(profileOptions || []).map((opt) => (
                        <option key={opt.key} value={opt.key}>{opt.label || opt.key}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1 px-1">
                    <select
                      value={p.material_key || ''}
                      onChange={(e) => updateManualPin(idx, { material_key: e.target.value })}
                      disabled={busy || !(materialOptions && materialOptions.length)}
                      data-testid={`gutter-manual-pin-material-${idx}`}
                      className="w-full px-1.5 py-0.5 text-[11px] border border-slate-300 rounded bg-white disabled:bg-slate-100"
                    >
                      <option value="">— use top-level —</option>
                      {(materialOptions || []).map((opt) => (
                        <option key={opt.key} value={opt.key}>{opt.label || opt.key}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1 px-1 text-center">
                    <button
                      type="button"
                      onClick={() => removeManualPin(idx)}
                      disabled={busy}
                      data-testid={`gutter-manual-pin-remove-${idx}`}
                      title="Remove this run"
                      className="text-rose-600 hover:text-rose-800 disabled:opacity-50 text-sm font-bold leading-none"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Manual outlets */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Outlets / Downpipes</span>
          <button
            type="button"
            data-testid="gutter-manual-outlet-add"
            onClick={addManualOutlet}
            disabled={busy}
            className="text-[10px] font-semibold px-2 py-0.5 rounded border border-amber-400 bg-white text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          >
            + Add outlet
          </button>
        </div>
        {manualOutlets.length === 0 ? (
          <p className="text-[10px] italic text-slate-500 px-2 py-1">No manual outlets yet.</p>
        ) : (
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-slate-500">
                <th className="text-left py-1 px-1 font-semibold w-32">Label</th>
                <th className="text-right py-1 px-1 font-semibold w-24">Ø (mm)</th>
                <th className="text-left py-1 px-1 font-semibold w-24">Shape</th>
                <th className="text-right py-1 px-1 font-semibold w-20">Qty</th>
                <th />
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {manualOutlets.map((o, idx) => (
                <tr key={idx} className="border-t border-amber-100" data-testid={`gutter-manual-outlet-row-${idx}`}>
                  <td className="py-1 px-1">
                    <input
                      type="text"
                      value={o.label || ''}
                      onChange={(e) => updateManualOutlet(idx, { label: e.target.value })}
                      disabled={busy}
                      placeholder={`Outlet ${idx + 1}`}
                      data-testid={`gutter-manual-outlet-label-${idx}`}
                      className="w-full px-1.5 py-0.5 text-[11px] border border-slate-300 rounded bg-white"
                    />
                  </td>
                  <td className="py-1 px-1">
                    <select
                      value={o.diameter_mm || 80}
                      onChange={(e) => updateManualOutlet(idx, { diameter_mm: Number(e.target.value) })}
                      disabled={busy}
                      data-testid={`gutter-manual-outlet-diameter-${idx}`}
                      className="w-full px-1.5 py-0.5 text-[11px] border border-slate-300 rounded bg-white text-right"
                    >
                      <option value={65}>65</option>
                      <option value={80}>80</option>
                      <option value={100}>100</option>
                    </select>
                  </td>
                  <td className="py-1 px-1">
                    <select
                      value={o.shape || 'Round'}
                      onChange={(e) => updateManualOutlet(idx, { shape: e.target.value })}
                      disabled={busy}
                      data-testid={`gutter-manual-outlet-shape-${idx}`}
                      className="w-full px-1.5 py-0.5 text-[11px] border border-slate-300 rounded bg-white"
                    >
                      <option value="Round">Round</option>
                      <option value="Square">Square</option>
                    </select>
                  </td>
                  <td className="py-1 px-1">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={o.qty ?? 1}
                      onChange={(e) => updateManualOutlet(idx, { qty: e.target.value === '' ? 1 : Math.max(1, Number(e.target.value)) })}
                      disabled={busy}
                      data-testid={`gutter-manual-outlet-qty-${idx}`}
                      className="w-full px-1.5 py-0.5 text-[11px] border border-slate-300 rounded text-right font-mono-num"
                    />
                  </td>
                  <td />
                  <td className="py-1 px-1 text-center">
                    <button
                      type="button"
                      onClick={() => removeManualOutlet(idx)}
                      disabled={busy}
                      data-testid={`gutter-manual-outlet-remove-${idx}`}
                      title="Remove this outlet"
                      className="text-rose-600 hover:text-rose-800 disabled:opacity-50 text-sm font-bold leading-none"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-[10px] italic text-slate-500">
        Manual entries feed the same rate engine as diagram pins · click <strong>Save Config</strong> above to persist and refresh the preview total.
      </p>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// v10.29 Sprint 3 (2026-02c) — Stripping + Extra Hours section.
// Global stripping toggle ($/m removal of existing gutter) plus a
// multi-entry Extra Hours list (each entry = name · hours · rate).
// Both feed the backend `_calc_lines` engine on the very next
// preview / save so operators see totals refresh live.
// ─────────────────────────────────────────────────────────────────
function StrippingAndExtraHoursSection({ config, preview, update, busy }) {
  const entries = Array.isArray(config.extra_hours_entries) ? config.extra_hours_entries : [];
  const stats = preview?.stats || {};

  const patchEntry = (idx, patch) => {
    const next = entries.map((e, i) => (i === idx ? { ...e, ...patch } : e));
    update({ extra_hours_entries: next });
  };
  const addEntry = () => {
    update({ extra_hours_entries: [...entries, { name: '', hours: 0, rate: 95 }] });
  };
  const removeEntry = (idx) => {
    update({ extra_hours_entries: entries.filter((_, i) => i !== idx) });
  };
  const duplicateLast = () => {
    const last = entries[entries.length - 1];
    if (!last) return addEntry();
    update({ extra_hours_entries: [...entries, { ...last }] });
  };

  return (
    <div className="bg-white border border-slate-200 rounded p-3 space-y-3" data-testid="gutter-strip-extra-section">
      <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 pb-2">
        <h4 className="text-xs font-bold text-slate-700">Stripping + Extra Hours</h4>

        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={!!config.stripping_enabled}
                 data-testid="gutter-stripping-enabled"
                 onChange={(e) => update({ stripping_enabled: e.target.checked })}
                 disabled={busy} />
          <span className="font-semibold">Strip existing gutter</span>
        </label>

        {config.stripping_enabled && (
          <>
            <Field label="Rate $/m" width="7rem">
              <input type="number" step="0.5" min="0"
                     value={config.stripping_rate_per_m ?? 0}
                     data-testid="gutter-stripping-rate"
                     onChange={(e) => update({ stripping_rate_per_m: parseFloat(e.target.value) || 0 })}
                     disabled={busy}
                     className="px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num"
                     style={{ width: '7rem' }} />
            </Field>
            <div className="text-[10px] text-slate-500 font-mono-num pb-1"
                 data-testid="gutter-stripping-calc">
              {(stats.stripping_qty_m || 0).toFixed(2)} m × ${(config.stripping_rate_per_m || 0).toFixed(2)}/m ={' '}
              <strong className="text-slate-700">
                ${((stats.stripping_qty_m || 0) * (config.stripping_rate_per_m || 0)).toFixed(2)}
              </strong>
            </div>
          </>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
            Extra Hours (labour) · {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
          </div>
          <div className="flex items-center gap-1.5">
            <button type="button"
                    data-testid="gutter-extra-hours-duplicate"
                    onClick={duplicateLast}
                    disabled={busy || entries.length === 0}
                    className="px-2 py-1 text-[10px] font-bold rounded border border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-40">
              ⎘ Duplicate last
            </button>
            <button type="button"
                    data-testid="gutter-extra-hours-add"
                    onClick={addEntry}
                    disabled={busy}
                    className="px-2 py-1 text-[10px] font-bold rounded border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
              + Add hours entry
            </button>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="text-[10px] italic text-slate-400">
            No extra-hour entries. Add one to bill site-specific labour (scaffold access, cleanup, difficult access, etc.).
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="gutter-extra-hours-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="py-1 pr-2">Name</th>
                <th className="py-1 pr-2 text-right">Hours</th>
                <th className="py-1 pr-2 text-right">Rate $/hr</th>
                <th className="py-1 pr-2 text-right">Line $</th>
                <th className="py-1 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`gutter-extra-hours-row-${i}`}>
                  <td className="py-1 pr-2">
                    <input type="text" value={e.name || ''}
                           data-testid={`gutter-extra-hours-name-${i}`}
                           onChange={(ev) => patchEntry(i, { name: ev.target.value })}
                           placeholder="e.g. Scaffold access · rear elevation"
                           disabled={busy}
                           className="w-full px-1.5 py-0.5 text-xs border border-slate-300 rounded" />
                  </td>
                  <td className="py-1 pr-2 text-right">
                    <input type="number" step="0.25" min="0" value={e.hours ?? 0}
                           data-testid={`gutter-extra-hours-hours-${i}`}
                           onChange={(ev) => patchEntry(i, { hours: parseFloat(ev.target.value) || 0 })}
                           disabled={busy}
                           className="w-16 px-1 py-0.5 text-xs border border-slate-300 rounded text-right font-mono-num" />
                  </td>
                  <td className="py-1 pr-2 text-right">
                    <input type="number" step="1" min="0" value={e.rate ?? 0}
                           data-testid={`gutter-extra-hours-rate-${i}`}
                           onChange={(ev) => patchEntry(i, { rate: parseFloat(ev.target.value) || 0 })}
                           disabled={busy}
                           className="w-20 px-1 py-0.5 text-xs border border-slate-300 rounded text-right font-mono-num" />
                  </td>
                  <td className="py-1 pr-2 text-right font-mono-num font-bold text-emerald-800"
                      data-testid={`gutter-extra-hours-total-${i}`}>
                    {fmt((e.hours || 0) * (e.rate || 0))}
                  </td>
                  <td className="py-1 text-right">
                    <button type="button"
                            data-testid={`gutter-extra-hours-remove-${i}`}
                            onClick={() => removeEntry(i)}
                            disabled={busy}
                            title="Remove this entry"
                            className="w-6 h-6 text-rose-600 hover:bg-rose-50 rounded">
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// v10.29 Sprint 3 (2026-02c) — Consumables summary card. Full
// calculation transparency (Reroof-style) for rivets + silicone
// derived from the backend stats block. No FE recompute — trusts
// the values `_calc_lines` returns.
// ─────────────────────────────────────────────────────────────────
function ConsumablesSummaryCard({ preview }) {
  const s = preview?.stats || {};
  const outlets      = s.total_outlets      || 0;
  const stopEnds     = s.total_stop_ends    || 0;
  const mitres       = s.total_mitre_count  || 0;
  const rivetsRaw    = s.rivets_raw         || 0;
  const rivetsFinal  = s.rivets_final       || 0;
  const siliconeRaw  = s.silicone_raw       || 0;
  const siliconeFinal = s.silicone_final    || 0;
  const rivetBufPct  = s.rivet_buffer_pct   || 0;
  const silBufPct    = s.silicone_buffer_pct || 0;

  if (outlets === 0 && stopEnds === 0 && mitres === 0) {
    return null;
  }

  return (
    <div className="bg-amber-50/40 border border-amber-200 rounded p-3 space-y-2"
         data-testid="gutter-consumables-summary">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold uppercase tracking-wider text-amber-900">
          Consumables · rivets + silicone
        </h4>
        <div className="text-[10px] text-amber-700">
          {outlets} outlet{outlets === 1 ? '' : 's'} · {stopEnds} stop-end{stopEnds === 1 ? '' : 's'} · {mitres} mitre{mitres === 1 ? '' : 's'}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <div className="bg-white rounded border border-amber-200 p-2 space-y-1"
             data-testid="gutter-consumables-rivets">
          <div className="text-[10px] uppercase font-bold text-amber-900 tracking-wider">Rivets</div>
          <div className="font-mono-num text-slate-600 text-[10px]">
            {rivetsRaw.toFixed(1)} raw × (1 + {rivetBufPct.toFixed(0)}% buffer) = ⌈{(rivetsRaw * (1 + rivetBufPct / 100)).toFixed(1)}⌉
          </div>
          <div className="text-lg font-bold text-emerald-800 font-mono-num">
            {rivetsFinal} ea
          </div>
        </div>
        <div className="bg-white rounded border border-amber-200 p-2 space-y-1"
             data-testid="gutter-consumables-silicone">
          <div className="text-[10px] uppercase font-bold text-amber-900 tracking-wider">Silicone (Modified)</div>
          <div className="font-mono-num text-slate-600 text-[10px]">
            {siliconeRaw.toFixed(2)} raw → ⌈{siliconeRaw.toFixed(2)}⌉
            {siliconeFinal > Math.ceil(siliconeRaw) ? ` + 1 nudge (frac ≥ 1 − ${silBufPct.toFixed(0)}%)` : ''}
          </div>
          <div className="text-lg font-bold text-emerald-800 font-mono-num">
            {siliconeFinal} tube{siliconeFinal === 1 ? '' : 's'}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// v10.29 Sprint 3 (2026-02c) — "Line items pushed to Components"
// summary card. Mirrors the Reroof Builder pattern (see
// ReroofBuilderTab.js:1710). Shows every line the Push endpoint
// will send to component_lines + inline calc-shadow per row.
// ─────────────────────────────────────────────────────────────────
function PushedLinesSummaryCard({ preview }) {
  const lines = preview?.lines || [];
  if (lines.length === 0) return null;

  // Group order (roughly matches Push order): Gutter → Brackets →
  // Bracket Screws → Dropper → Downpipe → Bend → DP Bracket → Rainhead
  // → Rivets → Silicone → Stripping → Extra Hours.
  const groupOrder = [
    'Gutter', 'Brackets', 'Bracket Screws',
    'Dropper', 'Dropper Install', 'Downpipe', 'Downpipe Bend', 'Downpipe Bracket', 'Rainhead',
    'Rivets', 'Silicone (Modified)', 'Silicone (Clear)',
    'Stripping', 'Extra Hours',
  ];
  const bucketFor = (ct) => {
    if (!ct) return 'Other';
    if (ct.startsWith('Gutter —')) return 'Gutter';
    return ct;
  };
  const grouped = new Map();
  lines.forEach(l => {
    const b = bucketFor(l.component_type);
    if (!grouped.has(b)) grouped.set(b, []);
    grouped.get(b).push(l);
  });
  const sortedGroups = Array.from(grouped.keys()).sort((a, b) => {
    const ai = groupOrder.indexOf(a); const bi = groupOrder.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  const grand = lines.reduce((s, l) => s + (l.total || 0), 0);

  const qtyDisplay = (l) => {
    const q = Number(l.quantity || 0);
    const u = l.unit || '';
    if (u === 'm') return `${q.toFixed(2)} m`;
    if (u === 'ea') return `${q} ea`;
    if (u === 'hr') return `${q.toFixed(2)} hr`;
    return `${q} ${u}`.trim();
  };

  return (
    <div className="bg-white border border-slate-200 rounded p-3 space-y-2"
         data-testid="gutter-pushed-lines-summary">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
          Quote lines (added when you Confirm)
        </h4>
        <div className="text-xs font-mono-num text-slate-500">
          {lines.length} line{lines.length === 1 ? '' : 's'}
        </div>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="py-1 pr-2">Type</th>
            <th className="py-1 pr-2">Item</th>
            <th className="py-1 pr-2 text-right">Qty</th>
            <th className="py-1 pr-2 text-right">Rate</th>
            <th className="py-1 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {sortedGroups.map(g => (
            <React.Fragment key={g}>
              <tr className="bg-slate-50">
                <td colSpan={5} className="py-1 px-2 text-[10px] font-bold uppercase tracking-wider text-slate-600">
                  {g}
                </td>
              </tr>
              {grouped.get(g).map((l, i) => (
                <tr key={`${g}-${i}`} className="border-t border-slate-100 align-top"
                    data-testid={`gutter-pushed-line-${g.toLowerCase().replace(/\W+/g, '_')}-${i}`}>
                  <td className="py-1 pr-2 text-slate-700 font-semibold whitespace-nowrap">
                    {l.component_type}
                  </td>
                  <td className="py-1 pr-2 text-slate-700">
                    <div>{l.type_name || '—'}</div>
                    {l.notes && (
                      <div className="text-[10px] text-slate-400 italic mt-0.5"
                           data-testid={`gutter-pushed-line-calc-${g.toLowerCase().replace(/\W+/g, '_')}-${i}`}>
                        {l.notes}
                      </div>
                    )}
                  </td>
                  <td className="py-1 pr-2 text-right font-mono-num text-slate-700 whitespace-nowrap">
                    {qtyDisplay(l)}
                  </td>
                  <td className="py-1 pr-2 text-right font-mono-num text-slate-500 whitespace-nowrap">
                    {fmt(l.rate || 0)}
                  </td>
                  <td className="py-1 text-right font-mono-num font-semibold text-emerald-800 whitespace-nowrap">
                    {fmt(l.total || 0)}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-300 bg-emerald-50">
            <td colSpan={4} className="py-2 px-2 text-right font-bold uppercase tracking-wider text-[10px] text-slate-700">
              Grand total (ex GST)
            </td>
            <td className="py-2 text-right font-mono-num font-extrabold text-emerald-800 text-sm"
                data-testid="gutter-pushed-grand-total">
              {fmt(grand)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

